import type {
  Props as SelectProps,
  SelectOption,
} from '@sgcmpteltd/uikit-react/components/select';
import {
  MENU_BORDER_RADIUS_EXTRA_LARGE,
  MENU_ITEM_TEXT_OVERFLOW_CLAMP,
  Select,
} from '@sgcmpteltd/uikit-react/components/select';
import cx from 'clsx';
import { useMemo } from 'react';

import { PARALLEL_IMPORTERS } from '@/constants/global';

import styles from './styles.module.scss';
import type { Component } from './types';

export const NewCarsModelSelectWidget: Component = ({
  className,
  isMakeInvalid,
  isSubModelIdInvalid,
  list,
  make,
  subModelIdAndReviewCarId,
  headerClassName,
  menuContainerClassName,
  menuFit,
  onMakeChange,
  onSubModelIdChange,
}) => {
  const brandOptionList = useMemo<SelectProps['optionList']>(() => {
    const subModelBrandSet = new Set(list.map(({ make: brand }) => brand));

    return (
      Array.from(subModelBrandSet)
        .sort((brandA, brandB) => brandA.localeCompare(brandB))
        .map(brand => ({
          value: brand,
          text: brand,
        }))
    );
  }, [list]);

  const makeModelOptionList = useMemo<SelectProps['optionList']>(() => {
    const newModels = new Set<string>();

    return list
      .filter(({ make: optionMake }) => optionMake === make)
      .reduce((acc, {
        reviewCarId,
        id,
        model,
        dealerType,
      }) => {
        if (!newModels.has(`${model}${dealerType === PARALLEL_IMPORTERS ? ' (PI)' : ''}`)) {
          newModels.add(`${model}${dealerType === PARALLEL_IMPORTERS ? ' (PI)' : ''}`);
          (acc as SelectOption[]).push({
            value: `${id},${reviewCarId}`,
            text: `${model}${dealerType === PARALLEL_IMPORTERS ? ' (PI)' : ''}`,
          });
        }
        return acc;
      }, [] as SelectOption[])
      .sort(({ text: textA }, { text: textB }) => textA.localeCompare(textB));
  }, [list, make]);

  return (
    <div
      className={cx(
        styles.containerRoot,
        className,
      )}>
      <Select
        className={styles.containerSelectRoot}
        headerClassName={cx(
          styles.selectRoot,
          styles.containerSelectHead,
          headerClassName,
        )}
        isInvalid={isMakeInvalid}
        menuBorderRadius={MENU_BORDER_RADIUS_EXTRA_LARGE}
        menuClassName={cx(
          styles.containerSelectMenu,
          styles.isMake,
          menuContainerClassName,
        )}
        menuFit={menuFit}
        menuItemTextOverflow={MENU_ITEM_TEXT_OVERFLOW_CLAMP}
        optionList={brandOptionList}
        placeholder={{
          text: 'Car Make',
          isOptionHidden: true,
        }}
        searchMode={{
          isCaseSensitive: false,
        }}
        value={make ?? ''}
        autoScrollToActiveItem
        onChange={(newMake) => {
          if (typeof newMake !== 'string') {
            return;
          }

          onMakeChange?.(newMake);

          if (newMake === make) {
            return;
          }

          onSubModelIdChange?.(null);
        }} />
      <Select
        className={styles.containerSelectRoot}
        headerClassName={cx(
          styles.selectRoot,
          styles.containerSelectHead,
          !make && styles.isDisabled,
          headerClassName,
        )}
        isDisabled={!make}
        isInvalid={isSubModelIdInvalid}
        menuBorderRadius={MENU_BORDER_RADIUS_EXTRA_LARGE}
        menuClassName={cx(
          styles.containerSelectMenu,
          styles.isSubModel,
          menuContainerClassName,
        )}
        menuFit={menuFit}
        menuItemTextOverflow={MENU_ITEM_TEXT_OVERFLOW_CLAMP}
        optionList={makeModelOptionList}
        placeholder={{
          text: 'Car Model',
          isOptionHidden: true,
        }}
        value={subModelIdAndReviewCarId}
        autoScrollToActiveItem
        onChange={(newModelIdAndReviewCarId) => {
          const idTuple = (newModelIdAndReviewCarId as string).split(',');

          onSubModelIdChange?.({
            modelId: Number(idTuple[0]),
            reviewCarId: Number(idTuple[1]),
          });
        }} />
    </div>
  );
};
